import React from 'react';
import PropTypes from 'prop-types';
import { Table, Button } from 'react-bootstrap';

/**
 * MetadataFieldTable - Presentational component for displaying metadata field data
 * 
 * @param {Array} fields - Array of metadata field objects
 * @param {boolean} loading - Loading state indicator
 * @param {Function} onCreate - Callback when add button is clicked
 * @param {Function} onEdit - Callback when edit button is clicked
 * @param {Function} onDelete - Callback when delete button is clicked
 * @param {Function} onExport - Callback when export button is clicked
 * @param {Function} onImport - Callback when import button is clicked
 * @param {Function} onDownloadSample - Callback when download sample button is clicked
 */
const MetadataFieldTable = ({ 
  fields, 
  loading, 
  onCreate, 
  onEdit, 
  onDelete,
  onExport,
  onImport,
  onDownloadSample
}) => {
  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>Metadata Fields</h5>
        <div>
          {onDownloadSample && (
            <Button 
              variant="outline-info" 
              size="sm"
              className="me-2"
              onClick={onDownloadSample}
            >
              Download Sample
            </Button>
          )}
          {onExport && (
            <Button 
              variant="outline-warning" 
              size="sm"
              className="me-2"
              onClick={onExport}
              disabled={!fields || fields.length === 0}
            >
              Export Metadata
            </Button>
          )}
          {onImport && (
            <>
              <Button 
                variant="outline-secondary" 
                size="sm"
                className="me-2"
                onClick={() => document.getElementById('importMetadataFile').click()}
              >
                Import Configurations
              </Button>
              <input
                id="importMetadataFile"
                type="file"
                accept=".json"
                style={{ display: 'none' }}
                onChange={onImport}
              />
            </>
          )}
          <Button variant="primary" size="sm" onClick={onCreate}>
            Add Metadata Field
          </Button>
        </div>
      </div>

      {!fields || fields.length === 0 ? (
        <div className="text-center p-4 text-muted">
          No metadata fields configured. Click "Add Metadata Field" to create one.
        </div>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Field Name</th>
              <th>Field Key</th>
              <th>Data Type</th>
              <th>Description</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {fields.map(field => (
              <tr key={field.id}>
                <td>{field.name}</td>
                <td><code>{field.fieldKey}</code></td>
                <td>{field.dataType}</td>
                <td className="text-truncate" style={{ maxWidth: '300px' }}>
                  {field.description || '-'}
                </td>
                <td>
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    className="me-2"
                    onClick={() => onEdit(field)}
                  >
                    Edit
                  </Button>
                  <Button 
                    variant="outline-danger" 
                    size="sm"
                    onClick={() => onDelete(field.id)}
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
};

MetadataFieldTable.propTypes = {
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      fieldKey: PropTypes.string.isRequired,
      dataType: PropTypes.string.isRequired,
      description: PropTypes.string
    })
  ).isRequired,
  loading: PropTypes.bool,
  onCreate: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onExport: PropTypes.func,
  onImport: PropTypes.func,
  onDownloadSample: PropTypes.func
};

MetadataFieldTable.defaultProps = {
  loading: false,
  onExport: null,
  onImport: null,
  onDownloadSample: null
};

export default MetadataFieldTable;
